import React from 'react';

// Render inline markdown: **bold**, *italic*, `code`
function inlineMdCore(text, baseKey = 0) {
  const parts = [];
  let i = 0, key = baseKey, buf = '';

  while (i < text.length) {
    // **bold**
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        if (buf) { parts.push(buf); buf = ''; }
        parts.push(<strong key={key++}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // *italic* (not part of **)
    if (text[i] === '*' && text[i + 1] !== '*' && (i === 0 || text[i - 1] !== '*')) {
      const end = text.indexOf('*', i + 1);
      if (end !== -1 && text[end + 1] !== '*') {
        if (buf) { parts.push(buf); buf = ''; }
        parts.push(<em key={key++}>{text.slice(i + 1, end)}</em>);
        i = end + 1;
        continue;
      }
    }
    // `code`
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end !== -1) {
        if (buf) { parts.push(buf); buf = ''; }
        parts.push(
          <code key={key++} style={{ background: 'rgba(0,0,0,.08)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace', fontSize: '0.88em' }}>
            {text.slice(i + 1, end)}
          </code>
        );
        i = end + 1;
        continue;
      }
    }
    buf += text[i++];
  }
  if (buf) parts.push(buf);
  return parts;
}

// Wraps inlineMdCore to handle <br> tags as real line breaks
function inlineMd(text, baseKey = 0) {
  if (!/<br\s*\/?>/i.test(text)) return inlineMdCore(text, baseKey);
  const segs = text.split(/<br\s*\/?>/i);
  const result = [];
  segs.forEach((seg, idx) => {
    if (idx > 0) result.push(<br key={`br-${baseKey}-${idx}`} />);
    result.push(...inlineMdCore(seg, baseKey + (idx + 1) * 1000));
  });
  return result;
}

// Parse a pipe-delimited table row into cells
function parseTableRow(line) {
  return line.trim().replace(/^\||\|$/g, '').split('|')
    .map(c => c.replace(/<br\s*\/?>/gi, ' ').trim());
}

const isTableLine = (line) => /^\s*\|.+\|\s*$/.test(line);
const isSeparatorLine = (line) => /^\s*\|[\s\-:|]+\|\s*$/.test(line);

function renderTable(rows, key) {
  if (!rows.length) return null;
  const header = rows[0];
  const body = rows.filter((_, i) => i > 0 && !rows[i].every(c => /^[-:]+$/.test(c)));
  return (
    <div key={key} style={{ overflowX: 'auto', margin: '6px 0' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12.5, width: '100%', minWidth: 280 }}>
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} style={{ border: '1px solid var(--border-color)', padding: '5px 10px', background: 'var(--bg-secondary)', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {inlineMd(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)' }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ border: '1px solid var(--border-light)', padding: '5px 10px', verticalAlign: 'top' }}>
                  {inlineMd(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Full block-level markdown renderer: bullets, numbered lists, headings, tables, paragraphs
export function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const output = [];
  let ulItems  = null;
  let olItems  = null;
  let tableRows = null;

  const flushUl = () => {
    if (!ulItems) return;
    output.push(
      <ul key={output.length} style={{ margin: '4px 0', paddingLeft: 20 }}>
        {ulItems.map((t, i) => <li key={i} style={{ marginBottom: 2 }}>{inlineMd(t)}</li>)}
      </ul>
    );
    ulItems = null;
  };

  const flushOl = () => {
    if (!olItems) return;
    output.push(
      <ol key={output.length} style={{ margin: '4px 0', paddingLeft: 20 }}>
        {olItems.map((t, i) => <li key={i} style={{ marginBottom: 2 }}>{inlineMd(t)}</li>)}
      </ol>
    );
    olItems = null;
  };

  const flushTable = () => {
    if (!tableRows) return;
    output.push(renderTable(tableRows, output.length));
    tableRows = null;
  };

  for (const line of lines) {
    if (isTableLine(line)) {
      flushUl(); flushOl();
      if (!tableRows) tableRows = [];
      if (!isSeparatorLine(line)) {
        tableRows.push(parseTableRow(line));
      }
      continue;
    }

    flushTable();

    const ulM = line.match(/^[-*•]\s+(.*)/);
    const olM = line.match(/^\d+[.)]\s+(.*)/);
    const hM  = line.match(/^(#{1,4})\s+(.*)/);

    if (ulM) {
      flushOl();
      if (!ulItems) ulItems = [];
      ulItems.push(ulM[1]);
    } else if (olM) {
      flushUl();
      if (!olItems) olItems = [];
      olItems.push(olM[1]);
    } else {
      flushUl(); flushOl();
      if (hM) {
        const sz = [16, 15, 14, 13][hM[1].length - 1] ?? 13;
        output.push(
          <p key={output.length} style={{ margin: '8px 0 2px', fontWeight: 700, fontSize: sz }}>
            {inlineMd(hM[2])}
          </p>
        );
      } else if (line.trim() === '') {
        if (output.length > 0) output.push(<div key={output.length} style={{ height: 5 }} />);
      } else {
        output.push(
          <p key={output.length} style={{ margin: output.length === 0 ? 0 : '3px 0 0' }}>
            {inlineMd(line)}
          </p>
        );
      }
    }
  }
  flushUl(); flushOl(); flushTable();
  return output;
}
